import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, ChartOptions, Plugin } from 'chart.js/auto';
import { Observable, Subscription } from 'rxjs';

export type UppChartType = 'bar' | 'line' | 'pie' | 'doughnut';
export type UppChartLegendPosition = 'top' | 'bottom' | 'left' | 'right';
export type UppChartData<TType extends UppChartType = UppChartType> = ChartData<TType>;
export type UppChartOptions<TType extends UppChartType = UppChartType> = ChartOptions<TType>;
export type UppChartPlugin<TType extends UppChartType = UppChartType> = Plugin<TType>;
export type UppChartUpdate<TType extends UppChartType = UppChartType> = Partial<Pick<ChartConfiguration<TType>, 'type' | 'data' | 'options' | 'plugins'>>;
export type UppChartExportSize = {
    width?: number;
    height?: number;
};

export const UPP_CHART_REPORT_GOOGLE_COLORS = [
    '#3366cc', '#dc3912', '#ff9900', '#109618', '#990099', '#0099c6', '#dd4477', '#66aa00',
    '#b82e2e', '#316395', '#3366cc', '#994499', '#22aa99', '#aaaa11', '#6633cc', '#e67300',
    '#8b0707', '#651067', '#329262', '#5574a6', '#3b3eac', '#b77322', '#16d620', '#b91383',
    '#f4359e', '#9c5935', '#a9c413', '#2a778d', '#668d1c', '#bea413', '#0c5922', '#743411'
] as const;

interface UppChartLegendItem {
    label: string;
    color: string;
    value: number | null;
}

/**
 * Returns a repeated palette with the requested alpha.
 *
 * @param count Number of colors to return.
 * @param alpha Color alpha channel in range 0..1.
 */
export function uppChartPalette(count: number, alpha = 0.8): string[] {
    if (count <= 0){
        return [];
    }

    const colors: string[] = [];
    for (let idx = 0; idx < count; idx++){
        const hex = UPP_CHART_REPORT_GOOGLE_COLORS[idx % UPP_CHART_REPORT_GOOGLE_COLORS.length];
        colors.push(uppChartHexToRgba(hex, alpha));
    }
    return colors;
}

function uppChartHexToRgba(hex: string, alpha = 1): string {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Reusable Chart.js wrapper for upp-wdgt.
 *
 * It creates and destroys the Chart.js instance according to Angular lifecycle,
 * updates it from input and stream changes, and can render an internal custom legend.
 */
@Component({
    selector: 'upp-chart',
    templateUrl: './upp-chart.html',
    styleUrls: ['./upp-chart.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

    /**
     * Chart.js chart type.
     *
     * @default `'line'`
     */
    @Input() type: UppChartType = 'line';

    /**
     * Chart.js data object (labels + datasets).
     */
    @Input() data: UppChartData = {
        labels: [],
        datasets: []
    };

    /**
     * Chart.js options object.
     */
    @Input() options: UppChartOptions = {
        responsive: true,
        maintainAspectRatio: false
    };

    /**
     * Optional Chart.js plugins for this chart instance.
     */
    @Input() plugins: UppChartPlugin[] = [];

    /**
     * Optional stream-based updates (v3 report pattern).
     */
    @Input() update: Observable<UppChartUpdate> | null = null;

    /**
     * When enabled, chart animation is disabled and image output is emitted.
     */
    @Input() print = false;

    /**
     * Enables custom legend rendering inside `upp-chart`.
     */
    @Input() legend = false;

    /**
     * Custom legend placement relative to the chart area.
     */
    @Input() legendPosition: UppChartLegendPosition = 'bottom';

    /**
     * Maximum number of legend lines/items. Set `0` for unlimited.
     */
    @Input() legendMaxLines = 0;

    /**
     * Label used for grouped remainder when legend exceeds max lines.
     * Pass localized text from the consumer.
     */
    @Input() legendOtherLabel = '';

    /**
     * Color used by grouped remainder legend item.
     */
    @Input() legendOtherColor = 'rgba(152, 154, 162, 0.8)';

    /**
     * Shows dataset value next to the custom legend label.
     */
    @Input() legendShowValue = false;

    /**
     * Prefix text used before custom legend values.
     */
    @Input() legendValuePrefix = '';

    /**
     * Suffix text used after custom legend values.
     */
    @Input() legendValueSuffix = '';

    /**
     * Fixed decimal precision for custom legend values.
     * Set `null` to keep the raw numeric rendering.
     */
    @Input() legendValueDecimals: number | null = null;

    /**
     * CSS width of the host element.
     */
    @Input() width = '100%';

    /**
     * CSS height of the chart area.
     */
    @Input() height = '260px';

    /**
     * Emits PNG data URL when chart render is complete in print mode.
     */
    @Output() ready = new EventEmitter<string>();

    @HostBinding('style.width')
    get hostWidth(): string {
        return this.width;
    }

    @HostBinding('style.--upp-chart-height')
    get chartHeight(): string {
        return this.height;
    }

    get chartLayoutClass(): string {
        return `--legend-${this.legendPosition}`;
    }

    get legendItems(): UppChartLegendItem[] {
        if (!this.legend){
            return [];
        }

        const items = this.buildLegendItems();
        return this.applyLegendLimit(items);
    }

    formatLegendValue(value: number | null): string {
        if (value == null || Number.isNaN(Number(value))){
            return '';
        }

        const numeric = Number(value);
        const decimals = this.legendValueDecimals;
        const rendered = (typeof decimals === 'number' && decimals >= 0)
            ? numeric.toFixed(decimals)
            : String(numeric);

        return `${this.legendValuePrefix || ''}${rendered}${this.legendValueSuffix || ''}`;
    }

    private chart: Chart | null = null;
    private initialized = false;
    private updateSubscription: Subscription | null = null;

    constructor(private change: ChangeDetectorRef) {
        // nothing to do
    }

    ngOnInit(): void {
        this.bindUpdateStream();
    }

    ngAfterViewInit(): void {
        this.initialized = true;
        this.createChart();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['update']){
            this.bindUpdateStream();
        }

        if (!this.initialized || !this.chart){
            return;
        }

        if (changes['type'] || changes['plugins']){
            this.createChart();
            return;
        }

        if (changes['data']){
            this.chart.data = this.data;
        }
        if (changes['options'] || changes['legend']){
            this.chart.options = this.resolvedOptions();
        }

        if (changes['data'] || changes['options'] || changes['legend']){
            this.chart.update();
        }

        this.change.markForCheck();
    }

    /**
     * Forces chart re-render using the current inputs.
     */
    refresh(): void {
        if (!this.initialized){
            return;
        }

        this.createChart();
    }

    /**
     * Exports the current chart canvas as a base64 data URL.
     */
    getImageDataUrl(
        format: 'image/png' | 'image/jpeg' = 'image/png',
        quality = 0.92,
        exportSize: UppChartExportSize | null = null
    ): string | null {
        const sourceCanvas = this.canvas?.nativeElement;
        if (!sourceCanvas){
            return null;
        }

        const targetSize = this.resolveExportSize(sourceCanvas, exportSize);
        if (!targetSize){
            return sourceCanvas.toDataURL(format, quality);
        }

        return this.exportChartImageWithSize(format, quality, targetSize.width, targetSize.height);
    }

    /**
     * Triggers browser download for the current chart image.
     */
    downloadImage(
        fileName = 'chart',
        format: 'image/png' | 'image/jpeg' = 'image/png',
        quality = 0.92,
        exportSize: UppChartExportSize | null = null
    ): boolean {
        const dataUrl = this.getImageDataUrl(format, quality, exportSize);
        if (!dataUrl){
            return false;
        }

        const extension = format === 'image/jpeg' ? 'jpg' : 'png';
        const anchor = document.createElement('a');
        anchor.href = dataUrl;
        anchor.download = `${fileName}.${extension}`;
        anchor.click();
        return true;
    }

    ngOnDestroy(): void {
        this.clearUpdateSubscription();
        this.destroyChart();
    }

    private createChart(): void {
        if (!this.canvas){
            return;
        }

        this.destroyChart();

        const config: ChartConfiguration = {
            type: this.type,
            data: this.data,
            options: this.resolvedOptions(),
            plugins: this.plugins
        };

        this.chart = new Chart(this.canvas.nativeElement, config);

        if (this.print){
            const dataUrl = this.getImageDataUrl('image/png');
            if (dataUrl){
                setTimeout(() => this.ready.emit(dataUrl), 0);
            }
        }

        this.change.markForCheck();
    }

    private destroyChart(): void {
        if (this.chart){
            this.chart.destroy();
            this.chart = null;
        }
    }

    private bindUpdateStream(): void {
        this.clearUpdateSubscription();

        if (!this.update){
            return;
        }

        this.updateSubscription = this.update.subscribe((next) => {
            this.applyStreamUpdate(next);
        });
    }

    private clearUpdateSubscription(): void {
        if (this.updateSubscription){
            this.updateSubscription.unsubscribe();
            this.updateSubscription = null;
        }
    }

    private applyStreamUpdate(next: UppChartUpdate): void {
        if (next.type){
            this.type = next.type as UppChartType;
        }
        if (next.data){
            this.data = next.data;
        }
        if (next.options){
            this.options = next.options;
        }
        if (next.plugins){
            this.plugins = next.plugins as UppChartPlugin[];
        }

        if (!this.initialized){
            return;
        }

        if (next.type || next.plugins){
            this.createChart();
            return;
        }

        if (this.chart){
            if (next.data){
                this.chart.data = this.data;
            }
            if (next.options){
                this.chart.options = this.resolvedOptions();
            }
            this.chart.update();
        }

        this.change.markForCheck();
    }

    private resolvedOptions(): UppChartOptions {
        const resolved: UppChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            ...(this.options || {})
        };

        if (this.print){
            resolved.animation = false;
        }

        if (this.legend){
            const plugins = { ...(resolved.plugins || {}) } as Record<string, unknown>;
            const legend = { ...((plugins['legend'] as Record<string, unknown>) || {}), display: false };
            plugins['legend'] = legend;
            resolved.plugins = plugins;
        }

        return resolved;
    }

    private resolveExportSize(
        sourceCanvas: HTMLCanvasElement,
        exportSize: UppChartExportSize | null
    ): { width: number; height: number } | null {
        if (!exportSize){
            return null;
        }

        const width = Number(exportSize.width);
        const height = Number(exportSize.height);
        if (!(width > 0) || !(height > 0)){
            return null;
        }

        const sourceWidth = Math.max(1, Math.round(sourceCanvas.width || sourceCanvas.clientWidth || 0));
        const sourceHeight = Math.max(1, Math.round(sourceCanvas.height || sourceCanvas.clientHeight || 0));
        if (sourceWidth === Math.round(width) && sourceHeight === Math.round(height)){
            return null;
        }

        return {
            width: Math.max(1, Math.round(width)),
            height: Math.max(1, Math.round(height))
        };
    }

    private exportChartImageWithSize(
        format: 'image/png' | 'image/jpeg',
        quality: number,
        width: number,
        height: number
    ): string | null {
        if ((typeof document === 'undefined')){
            return null;
        }

        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width = width;
        offscreenCanvas.height = height;

        let exportChart: Chart | null = null;
        try {
            exportChart = new Chart(offscreenCanvas, {
                type: this.type,
                data: this.data,
                options: {
                    ...(this.resolvedOptions() || {}),
                    responsive: false,
                    maintainAspectRatio: false,
                    animation: false
                },
                plugins: this.plugins
            });
            return offscreenCanvas.toDataURL(format, quality);
        }
        catch {
            return null;
        }
        finally {
            if (exportChart){
                exportChart.destroy();
            }
        }
    }

    private buildLegendItems(): UppChartLegendItem[] {
        if (!this.data || !Array.isArray(this.data.datasets) || this.data.datasets.length === 0){
            return [];
        }

        const firstDataset = this.data.datasets[0] as unknown as Record<string, unknown>;
        const labels = Array.isArray(this.data.labels) ? this.data.labels : [];
        const usePointLegend = (
            this.type === 'pie' ||
            this.type === 'doughnut' ||
            (this.data.datasets.length === 1 && Array.isArray(firstDataset['backgroundColor']))
        );

        if (usePointLegend){
            return this.buildPointLegendItems(labels, firstDataset);
        }

        return this.buildDatasetLegendItems();
    }

    private buildPointLegendItems(labels: unknown[], dataset: Record<string, unknown>): UppChartLegendItem[] {
        const data = Array.isArray(dataset['data']) ? (dataset['data'] as unknown[]) : [];
        const colors = this.normalizeColorList(dataset['backgroundColor'], data.length);
        const items: UppChartLegendItem[] = [];

        for (let idx = 0; idx < data.length; idx++){
            const label = String(labels[idx] ?? `${idx + 1}`);
            items.push({
                label,
                color: colors[idx] || 'rgba(152, 154, 162, 0.8)',
                value: Number(data[idx]) || 0
            });
        }

        return items;
    }

    private buildDatasetLegendItems(): UppChartLegendItem[] {
        const items: UppChartLegendItem[] = [];

        for (let idx = 0; idx < this.data.datasets.length; idx++){
            const dataset = this.data.datasets[idx] as unknown as Record<string, unknown>;
            const label = String(dataset['label'] ?? `${idx + 1}`);
            items.push({
                label,
                color: this.pickDatasetColor(dataset),
                value: this.sumDatasetValues(dataset)
            });
        }

        return items;
    }

    private applyLegendLimit(items: UppChartLegendItem[]): UppChartLegendItem[] {
        const max = Number(this.legendMaxLines) || 0;
        if (max <= 0 || items.length <= max){
            return items;
        }

        const visibleCount = Math.max(0, max - 1);
        const head = items.slice(0, visibleCount);
        const tail = items.slice(visibleCount);
        const otherValue = tail.reduce((sum, item) => sum + (item.value || 0), 0);
        const otherLabel = this.legendOtherLabel?.trim() || `+${tail.length}`;

        head.push({
            label: otherLabel,
            color: this.legendOtherColor,
            value: otherValue
        });

        return head;
    }

    private pickDatasetColor(dataset: Record<string, unknown>): string {
        const bg = dataset['backgroundColor'];
        if (Array.isArray(bg) && bg.length > 0){
            return String(bg[0]);
        }
        if (typeof bg === 'string'){
            return bg;
        }

        const border = dataset['borderColor'];
        if (Array.isArray(border) && border.length > 0){
            return String(border[0]);
        }
        if (typeof border === 'string'){
            return border;
        }

        return 'rgba(152, 154, 162, 0.8)';
    }

    private normalizeColorList(color: unknown, length: number): string[] {
        if (Array.isArray(color)){
            return color.map((item) => String(item));
        }
        if (typeof color === 'string'){
            return Array.from({ length }, () => color);
        }

        return Array.from({ length }, () => 'rgba(152, 154, 162, 0.8)');
    }

    private sumDatasetValues(dataset: Record<string, unknown>): number | null {
        const values = Array.isArray(dataset['data']) ? (dataset['data'] as unknown[]) : null;
        if (!values){
            return null;
        }

        let total = 0;
        let hasValue = false;
        for (const value of values){
            const numeric = Number(value);
            if (!Number.isNaN(numeric)){
                total += numeric;
                hasValue = true;
            }
        }

        return hasValue ? total : null;
    }
}
